import type { SessionUser } from "@bodemleven/course-kit/schema";
import { atLeast } from "@bodemleven/course-kit/schema/core";
import { createContext, useContext, type ReactNode } from "react";

const CurrentUserContext = createContext<SessionUser | null>(null);

/** Provided by the /onderzoek gate once a session exists. */
export const CurrentUserProvider = ({
  user,
  children,
}: {
  user: SessionUser;
  children: ReactNode;
}): ReactNode => <CurrentUserContext value={user}>{children}</CurrentUserContext>;

export const useCurrentUser = (): SessionUser => {
  const user = useContext(CurrentUserContext);
  if (!user) throw new Error("useCurrentUser must be used inside the /onderzoek gate");
  return user;
};

/** Analysts and above see every land and do the lab work. */
export const useIsAnalyst = (): boolean => atLeast(useCurrentUser().role, "analyst");

/** Admins and above also do the Lab settings and appoint analysts. */
export const useIsAdmin = (): boolean => atLeast(useCurrentUser().role, "admin");

type Key = readonly string[];

/** Query keys of the signed-in user's workspace; `all` prefixes every other key. */
export interface WorkspaceKeys {
  all: Key;
  lands: () => Key;
  land: (id: string) => Key;
  samples: () => Key;
  sample: (id: string) => Key;
  researchList: () => Key;
  research: (id: string) => Key;
  adviceList: () => Key;
  advice: (id: string) => Key;
}

/**
 * Query keys carry the user id, so another account signing in on the same
 * browser never sees the previous user's cached lands or results. Route loaders
 * build them from the settled session (see workspace-loaders.ts).
 */
export const workspaceKeys = (userId: string): WorkspaceKeys => {
  const all = ["research-app", userId];
  return {
    all,
    lands: () => [...all, "lands"],
    land: (id) => [...all, "land", id],
    samples: () => [...all, "samples"],
    sample: (id) => [...all, "sample", id],
    researchList: () => [...all, "research-list"],
    research: (id) => [...all, "research", id],
    adviceList: () => [...all, "advice-list"],
    advice: (id) => [...all, "advice", id],
  };
};

export const useWorkspaceKeys = (): WorkspaceKeys => workspaceKeys(useCurrentUser().id);
